import { actions, containers } from '@ecosystem/newron-design-system';
import { clearAllFilters, filtersToRender, productsResponseConfig } from '@utils/FilterSettings';
import { onFilterTrayApply, onFilterTrayClear } from '@utils/filterTrayUtils/FilterTrayUtils';
import { FilterGroups, FilterKeys, FilterObject, Option, PageType, PageTypeAndName } from '@utils/pageConfig/Types';
import FilterComponentProduct from './FilterComponentProduct';

import { useAnalytics } from '@ecosystem/newron-client-lib';
import { getEffectiveProductType } from '@models/SubscribedProductType';
import { actionCreatorsSideFilters } from '@state/Index';
import { IRootState } from '@state/reducers/Reducers';
import { isBaseFiltersApplied } from '@utils/FilterUtils';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { getIsDisabledStatus } from '@utils/productGroupUtils/ProductGroupUtils';
import { isSelectedDiffWithApplied } from '@utils/validator/CommonValidator';
import getCurrencySymbolFromCode from 'currency-symbol-map';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { cloneDeep } from 'lodash';

const ListFiltersProduct: React.FC = () => {
  const defaultFilters = productsResponseConfig.response;
  const pagesConfig = usePageConfig();

  const dispatch = useDispatch();
  const { updateSideFilters } = bindActionCreators(actionCreatorsSideFilters, dispatch);
  const retailerClientState = useSelector((state: any) => state.retailerClient);
  const { track } = useAnalytics();
  const subscribedProductTypes = useSelector(
    (state: IRootState) => state.subscribedProductTypes.subscribedProductTypes,
  );

  const effectiveProductType = getEffectiveProductType(subscribedProductTypes, pagesConfig.pageName);

  const [filters, setFilters] = useState<FilterObject[]>(productsResponseConfig.response);

  const [currencySymbol, setCurrencySymbol] = useState<string>('');

  const getSelectedCountriesCurrency = (selectedCountry?: Option[]): string => {
    const hitlistCountry = pagesConfig.hitlistsClientCountries.filter((clientCountry) =>
      selectedCountry?.some((selectedClientCountry) => clientCountry.id === selectedClientCountry.value),
    );

    return hitlistCountry.length !== 0 ? (getCurrencySymbolFromCode(hitlistCountry[0].currency) as string) : '';
  };
  /*
   * updates the filter options when pg selection is changed in pg overview
   */
  useEffect(() => {
    if (pagesConfig.selectedPgUpdated) {
      const pageTypeAndName: PageTypeAndName = pagesConfig.getPageTypeAndName();

      setFilters(productsResponseConfig.response);
      pagesConfig.setSelectedPgUpdated(false);

      if (isBaseFiltersApplied(pageTypeAndName, pagesConfig.selectedFilters)) {
        if (isSelectedDiffWithApplied(pagesConfig, FilterKeys.PRODUCT_GROUP)) {
          const fil = clearAllFilters(pagesConfig.selectedFilters, pagesConfig.getPageTypeAndName());
          pagesConfig.setSelectedFilters(fil);
          pagesConfig.setApplyFilters(true);
          pagesConfig.setClearFilters(true);
        }
      }
    }
  }, [pagesConfig.selectedPgUpdated]);

  /*
   * updates the filter options when list of PGs are updated
   */
  useEffect(() => {
    if (pagesConfig.pgListUpdated) {
      const selectedCountry = pagesConfig.selectedFilters[pagesConfig.pageType].selectedFilters.find(
        (e) => e.filterKey === FilterKeys.COUNTRY,
      )?.options;
      const appliedCountry = pagesConfig.appliedFilters[pagesConfig.pageType].selectedFilters.find(
        (e) => e.filterKey === FilterKeys.COUNTRY,
      )?.options;

      // only clear the filters if the country selection change. this check prevents reset during page navigation
      if (selectedCountry !== appliedCountry) {
        const fil = clearAllFilters(pagesConfig.selectedFilters, pagesConfig.getPageTypeAndName());
        pagesConfig.setSelectedFilters(fil);
      }

      setCurrencySymbol(getSelectedCountriesCurrency(selectedCountry));

      pagesConfig.setPgListUpdated(false);
      setFilters(productsResponseConfig.response);
    }
  }, [pagesConfig.pgListUpdated]);

  const toRender = (filterObjects: FilterObject[]) => {
    return (
      <div>
        <div>
          {filtersToRender(pagesConfig.pageName, filterObjects).map((component) => (
            <div key={component.mainTitle}>
              <FilterComponentProduct
                filterObject={component}
                pageType={PageType.RI_PRODUCT}
                currencySymbol={currencySymbol}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  /**
   * Determines whether the user has access to the hitlist based on the filters.
   *
   * @returns {boolean} True if the user has access to the hitlist, false otherwise.
   */

  const getIsOpenFilters = (filtersArray: FilterObject[], filter: FilterObject) => {
    const isBaseMarketDefinitionFilters = filtersArray.filter(
      (specificFilter) =>
        specificFilter.filterGroup === FilterGroups.BASE || filter.filterGroup === FilterGroups.MARKET_DEFINITION,
    );

    const disabledSelection = getIsDisabledStatus(productsResponseConfig.response[2].filters);

    if (disabledSelection && !isBaseMarketDefinitionFilters) {
      return false;
    }
    return isBaseMarketDefinitionFilters.some((x) => x.filterGroup === filter.filterGroup);
  };
  /**
   * Renders the collapsible filters based on the provided filter objects.
   * @param filterObjects - An array of filter objects.
   * @returns An array of JSX elements representing the collapsible filters.
   *
   * -- To replace the existing one once side filter changes are finalized
   */
  const toRenderAccordionFilters = (filterObjects: FilterObject[]) => {
    const { Accordion } = containers;
    const filtersRenderArray = filtersToRender(pagesConfig.pageName, cloneDeep(filterObjects));
    return filtersRenderArray.map((component) => (
      <div key={component.mainTitle}>
        {component.mainTitle === '' || component.mainTitle === null ? (
          <div>
            <FilterComponentProduct
              filterObject={component}
              pageType={PageType.RI_PRODUCT}
              currencySymbol={currencySymbol}
            />
          </div>
        ) : (
          <Accordion flushAlign={true} key={component.mainTitle} hoverColor="bg-medium" iconRight={true}>
            <Accordion.Item
              key={component.mainTitle}
              title={component.mainTitle}
              data-testid="filter-accordion"
              isOpen={getIsOpenFilters(filtersRenderArray, component)}
            >
              <div>
                <FilterComponentProduct
                  filterObject={component}
                  pageType={PageType.RI_PRODUCT}
                  currencySymbol={currencySymbol}
                />
              </div>
            </Accordion.Item>
          </Accordion>
        )}
      </div>
    ));
  };

  const getFiltersUponPgUpdated = () => {
    if (pagesConfig.selectedPgUpdated) {
      return filters;
    }
    return defaultFilters;
  };

  const onApply = async () => {
    onFilterTrayApply(pagesConfig, updateSideFilters, retailerClientState, track, effectiveProductType);
  };

  const onClear = () => {
    onFilterTrayClear(pagesConfig);
  };

  const { Button } = actions;
  const { FilterTray } = containers;

  return (
    <>
      <FilterTray.Content>
        <div>
          {pagesConfig.isSideFiltersFeatureFlagEnabled
            ? toRenderAccordionFilters(getFiltersUponPgUpdated())
            : toRender(getFiltersUponPgUpdated())}
          {/* <div>{toRender(pagesConfig.selectedPgUpdated ? filters : defaultFilters)}</div> */}
        </div>
      </FilterTray.Content>
      <FilterTray.Footer>
        <div className="flex">
          <Button
            testId="cancel-filter"
            disabled={!pagesConfig.clearFilters}
            mode="secondary"
            stretch
            onClick={onClear}
          >
            Clear
          </Button>
          <Button type="submit" disabled={!pagesConfig.applyFilters} stretch onClick={onApply} testId="apply-filter">
            Apply
          </Button>
        </div>
      </FilterTray.Footer>
    </>
  );
};

export default ListFiltersProduct;
